// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { db } from '../../utils/firebase';
import { CRAWL_EXECUTIONS_COLLECTION } from '../../utils/firestoreCollections';
import { ClipboardCopyTableCell, clipboardCellInnerSx } from './ClipboardCopyTableCell';
import ExecutionCompareDialog from './ExecutionCompareDialog';
import { canShowExecutionCompare } from './executionCompareUtils';
import { CRAWL_TYPES } from './scheduleFormConstants';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/** Firestore equality filter when set; use __EMPTY__ only for client-side outcome filtering. */
const EXECUTION_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'success', label: 'success' },
  { value: 'failed', label: 'failed' },
  { value: 'running', label: 'running' },
];

const EXECUTION_OUTCOME_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: '__EMPTY__', label: 'No outcome' },
  { value: 'match', label: 'match' },
  { value: 'no match', label: 'no match' },
];

const SCAN_BATCH_SIZE = 50;
const SCAN_MAX_BATCHES = 40;

const OUTCOME_EMPTY_SENTINEL = '__EMPTY__';

/** Maps crawl schedule targetType to admin detail route; extend when new types ship. */
const TARGET_TYPE_DETAIL_PATH = {
  brewery: (targetId) => `/brewery/${encodeURIComponent(targetId)}`,
};

function targetDetailPath(targetType, targetId) {
  if (!targetId) {
    return null;
  }
  const build = targetType ? TARGET_TYPE_DETAIL_PATH[targetType] : null;
  return build ? build(targetId) : null;
}

function parseTargetTypeFromScheduleId(scheduleId) {
  if (scheduleId == null || String(scheduleId).trim() === '') {
    return '';
  }
  const segments = String(scheduleId).split('__');
  if (segments.length >= 3) {
    return segments[1];
  }
  return '';
}

function effectiveTargetType(row) {
  const fromDoc =
    row.targetType != null && String(row.targetType).trim() !== ''
      ? String(row.targetType).trim()
      : '';
  if (fromDoc) {
    return fromDoc;
  }
  return parseTargetTypeFromScheduleId(row.scheduleId);
}

function buildServerQueryConstraints(statusFilter, outcomeFilter, typeFilter) {
  const constraints = [];
  if (statusFilter) {
    constraints.push(where('status', '==', statusFilter));
  }
  if (typeFilter) {
    constraints.push(where('crawlType', '==', typeFilter));
  }
  if (outcomeFilter && outcomeFilter !== OUTCOME_EMPTY_SENTINEL) {
    constraints.push(where('outcome', '==', outcomeFilter));
  }
  return constraints;
}

function executionMatchesClientFilters(
  row,
  scheduleNeedleLower,
  targetNeedleLower,
  outcomeEmptyOnly,
) {
  if (scheduleNeedleLower) {
    const scheduleKey = String(row.scheduleId ?? '').toLowerCase();
    if (!scheduleKey.includes(scheduleNeedleLower)) {
      return false;
    }
  }
  if (targetNeedleLower) {
    const targetKey = String(row.targetId ?? '').toLowerCase();
    if (!targetKey.includes(targetNeedleLower)) {
      return false;
    }
  }
  if (outcomeEmptyOnly) {
    const hasOutcome = row.outcome != null && row.outcome !== '';
    if (hasOutcome) {
      return false;
    }
  }
  return true;
}

function mapExecutionDoc(docSnapshot) {
  const docData = docSnapshot.data();
  return {
    id: docSnapshot.id,
    scheduleId: docData.scheduleId ?? '',
    crawlType: docData.crawlType ?? '',
    targetType: docData.targetType ?? '',
    targetId: docData.targetId ?? '',
    startedAt: docData.startedAt?.toDate?.() ?? null,
    finishedAt: docData.finishedAt?.toDate?.() ?? null,
    status: docData.status ?? 'running',
    outcome: docData.outcome,
    entiteaTaskId: docData.entiteaTaskId,
    entiteaResult: docData.entiteaResult,
    errorMessage: docData.errorMessage,
    resolvedSearchQuery: docData.resolvedSearchQuery,
    placeCount: docData.placeCount,
    candidatesEvaluated: docData.candidatesEvaluated,
    matchedCandidateIndex: docData.matchedCandidateIndex,
    companyLocationId: docData.companyLocationId,
    socialsFoundCount: docData.socialsFoundCount,
    websiteCrawled: docData.websiteCrawled,
    socialsResult: docData.socialsResult,
  };
}

async function loadExecutionRowsWithClientFilters({
  executionsCollection,
  serverConstraints,
  effectiveSize,
  scheduleNeedleLower,
  targetNeedleLower,
  outcomeEmptyOnly,
  resumeDoc,
  loadId,
  latestLoadIdRef,
}) {
  let readCursor = resumeDoc;
  const matches = [];
  const matchingDocSnaps = [];
  let batches = 0;
  let lastBatchSize = 0;

  while (matches.length < effectiveSize + 1 && batches < SCAN_MAX_BATCHES) {
    batches += 1;
    const batchQuery =
      readCursor == null
        ? query(
            executionsCollection,
            ...serverConstraints,
            orderBy('startedAt', 'desc'),
            limit(SCAN_BATCH_SIZE),
          )
        : query(
            executionsCollection,
            ...serverConstraints,
            orderBy('startedAt', 'desc'),
            startAfter(readCursor),
            limit(SCAN_BATCH_SIZE),
          );

    const snap = await getDocs(batchQuery);
    if (loadId !== latestLoadIdRef.current) {
      return { stale: true };
    }

    lastBatchSize = snap.docs.length;
    if (snap.docs.length === 0) {
      break;
    }

    for (const docSnap of snap.docs) {
      const mapped = mapExecutionDoc(docSnap);
      if (
        executionMatchesClientFilters(
          mapped,
          scheduleNeedleLower,
          targetNeedleLower,
          outcomeEmptyOnly,
        )
      ) {
        matches.push(mapped);
        matchingDocSnaps.push(docSnap);
      }
    }

    readCursor = snap.docs[snap.docs.length - 1];
  }

  if (loadId !== latestLoadIdRef.current) {
    return { stale: true };
  }

  const moreInBuffer = matches.length > effectiveSize;
  const hitScanCap = batches >= SCAN_MAX_BATCHES;
  const hasNextPage =
    moreInBuffer ||
    (matches.length >= effectiveSize && lastBatchSize === SCAN_BATCH_SIZE) ||
    (hitScanCap && lastBatchSize === SCAN_BATCH_SIZE);

  const pageRows = moreInBuffer ? matches.slice(0, effectiveSize) : matches;
  const pageDocSnaps = moreInBuffer
    ? matchingDocSnaps.slice(0, effectiveSize)
    : matchingDocSnaps;

  const resumeAfterLastEmitted =
    pageDocSnaps.length > 0 ? pageDocSnaps[pageDocSnaps.length - 1] : readCursor;

  return { stale: false, pageRows, hasNextPage, readCursor: resumeAfterLastEmitted };
}

async function loadExecutionRowsSimpleQuery({
  executionsCollection,
  serverConstraints,
  targetPage,
  effectiveSize,
  previousLastDoc,
  loadId,
  latestLoadIdRef,
}) {
  const executionsQuery =
    targetPage === 0
      ? query(
          executionsCollection,
          ...serverConstraints,
          orderBy('startedAt', 'desc'),
          limit(effectiveSize + 1),
        )
      : query(
          executionsCollection,
          ...serverConstraints,
          orderBy('startedAt', 'desc'),
          startAfter(previousLastDoc),
          limit(effectiveSize + 1),
        );
  const snap = await getDocs(executionsQuery);
  if (loadId !== latestLoadIdRef.current) {
    return { stale: true };
  }
  const docs = snap.docs;
  const more = docs.length > effectiveSize;
  const pageDocs = more ? docs.slice(0, effectiveSize) : docs;
  const mapped = pageDocs.map(mapExecutionDoc);
  return { stale: false, mapped, pageDocs, hasNext: more };
}

function formatTs(value) {
  if (!value) return '—';
  return value instanceof Date ? value.toLocaleString() : String(value);
}

function formatCellText(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

const ellipsisCellSx = {
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const scheduleColumnSx = {
  maxWidth: 240,
  fontSize: '0.75rem',
};

const targetColumnSx = {
  maxWidth: 200,
};

function TargetExecutionTableCell({ row }) {
  const targetType = effectiveTargetType(row);
  const targetId = row.targetId;
  const linkTo = targetDetailPath(targetType, targetId);
  const display = targetId ? String(targetId) : '—';

  const handleBreweryLinkClick = useCallback(function handleBreweryLinkClick(event) {
    event.stopPropagation();
  }, []);

  if (!linkTo) {
    return <ClipboardCopyTableCell value={targetId} sx={targetColumnSx} />;
  }

  return (
    <TableCell sx={targetColumnSx}>
      <Tooltip title="Open brewery">
        <Box
          component={Link}
          to={linkTo}
          onClick={handleBreweryLinkClick}
          sx={{
            ...clipboardCellInnerSx,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {display}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

function formatSocialsSummary(row) {
  if (row.crawlType !== 'socials') {
    return '—';
  }
  const website = row.websiteCrawled != null && String(row.websiteCrawled).trim() !== ''
    ? String(row.websiteCrawled).trim()
    : '';
  const count =
    typeof row.socialsFoundCount === 'number' && Number.isFinite(row.socialsFoundCount)
      ? row.socialsFoundCount
      : null;
  if (website.length === 0 && count == null) {
    return '—';
  }
  if (count == null) {
    return website;
  }
  return `${count} social${count === 1 ? '' : 's'} · ${website || 'no website'}`;
}

function formatResolvedSearchQueryFromEntiteaResult(entiteaResult) {
  if (entiteaResult == null || typeof entiteaResult !== 'object') {
    return '';
  }
  const entiteaQuery = entiteaResult.query;
  if (entiteaQuery == null || typeof entiteaQuery !== 'object') {
    return '';
  }
  const parts = [entiteaQuery.breweryName, entiteaQuery.city, entiteaQuery.state]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter((part) => part.length > 0);
  return parts.join(' ');
}

function formatResolvedSearchOrSocials(row) {
  if (row.crawlType === 'socials') {
    return formatSocialsSummary(row);
  }
  const resolvedSearchQuery =
    row.resolvedSearchQuery != null && String(row.resolvedSearchQuery).trim() !== ''
      ? String(row.resolvedSearchQuery).trim()
      : formatResolvedSearchQueryFromEntiteaResult(row.entiteaResult);
  return formatCellText(resolvedSearchQuery);
}

function ExecutionTableRow({ row, onCompareClick }) {
  const formattedResolved = formatResolvedSearchOrSocials(row);
  const formattedError = formatCellText(row.errorMessage);
  const showCompare = canShowExecutionCompare(row);

  const handleCompareClick = useCallback(
    function handleCompareClick() {
      onCompareClick(row);
    },
    [onCompareClick, row],
  );

  return (
    <TableRow>
      <ClipboardCopyTableCell value={row.scheduleId} sx={scheduleColumnSx} />
      <TableCell>{row.crawlType}</TableCell>
      <TargetExecutionTableCell row={row} />
      <TableCell>{row.status}</TableCell>
      <TableCell>{formatCellText(row.outcome)}</TableCell>
      <TableCell>{formatTs(row.startedAt)}</TableCell>
      <TableCell
        sx={ellipsisCellSx}
        title={formattedResolved !== '—' ? formattedResolved : undefined}
      >
        {formattedResolved}
      </TableCell>
      <TableCell
        sx={{ ...ellipsisCellSx, maxWidth: 320 }}
        title={formattedError !== '—' ? formattedError : undefined}
      >
        {formattedError}
      </TableCell>
      <TableCell>
        {showCompare ? (
          <Button size="small" variant="outlined" onClick={handleCompareClick}>
            Compare
          </Button>
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}

function ExecutionsTableBody({ rows, onCompareClick }) {
  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={9}>
            <Typography variant="body2" color="text.secondary">
              No executions yet.
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  return (
    <TableBody>
      {rows.map(function executionRow(execRow) {
        return (
          <ExecutionTableRow
            key={execRow.id}
            row={execRow}
            onCompareClick={onCompareClick}
          />
        );
      })}
    </TableBody>
  );
}

function ExecutionTableColumnHeaders() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Schedule</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Target</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Outcome</TableCell>
        <TableCell>Started</TableCell>
        <TableCell>Resolved search / socials</TableCell>
        <TableCell>Error</TableCell>
        <TableCell>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

function CrawlExecutionsTable({ rows, onCompareClick }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
        <ExecutionTableColumnHeaders />
        <ExecutionsTableBody rows={rows} onCompareClick={onCompareClick} />
      </Table>
    </TableContainer>
  );
}

function ExecutionsPagerBar({ page, rowsLength, hasNext, onPrevious, onNext }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
      <Typography variant="body2" color="text.secondary">
        Page {page + 1}
        {rowsLength ? ` · ${rowsLength} row(s)` : ''}
      </Typography>
      <Box display="flex" gap={1}>
        <Button variant="outlined" disabled={page === 0} onClick={onPrevious}>
          Previous
        </Button>
        <Button variant="outlined" disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

function CrawlExecutionsTab() {
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [scheduleInput, setScheduleInput] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [compareExecution, setCompareExecution] = useState(null);

  const lastDocPerPage = useRef([]);
  const scanResumeAfterPage = useRef([]);
  const latestLoadIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setScheduleFilter(scheduleInput.trim());
      setTargetFilter(targetInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [scheduleInput, targetInput]);

  const loadPage = useCallback(
    async (targetPage) => {
      const effectiveSize = pageSize;
      const scheduleNeedleLower = scheduleFilter.toLowerCase();
      const targetNeedleLower = targetFilter.toLowerCase();
      const needsClientFilter =
        scheduleNeedleLower.length > 0 ||
        targetNeedleLower.length > 0 ||
        outcomeFilter === OUTCOME_EMPTY_SENTINEL;
      const outcomeEmptyOnly = outcomeFilter === OUTCOME_EMPTY_SENTINEL;

      const loadId = ++latestLoadIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const executionsCollection = collection(db, CRAWL_EXECUTIONS_COLLECTION);
        const serverConstraints = buildServerQueryConstraints(
          statusFilter,
          outcomeFilter,
          typeFilter,
        );

        if (targetPage === 0) {
          lastDocPerPage.current = [];
          scanResumeAfterPage.current = [];
        }

        if (needsClientFilter) {
          const resumeDoc =
            targetPage === 0 ? null : scanResumeAfterPage.current[targetPage - 1];
          if (targetPage > 0 && resumeDoc === undefined) {
            return;
          }

          const filtered = await loadExecutionRowsWithClientFilters({
            executionsCollection,
            serverConstraints,
            effectiveSize,
            scheduleNeedleLower,
            targetNeedleLower,
            outcomeEmptyOnly,
            resumeDoc,
            loadId,
            latestLoadIdRef,
          });
          if (filtered.stale) {
            return;
          }

          scanResumeAfterPage.current[targetPage] = filtered.readCursor;
          scanResumeAfterPage.current.length = targetPage + 1;

          setRows(filtered.pageRows);
          setHasNext(filtered.hasNextPage);
          setPage(targetPage);
          return;
        }

        const previousLastDoc =
          targetPage === 0 ? null : lastDocPerPage.current[targetPage - 1];
        if (targetPage > 0 && !previousLastDoc) {
          return;
        }

        const simple = await loadExecutionRowsSimpleQuery({
          executionsCollection,
          serverConstraints,
          targetPage,
          effectiveSize,
          previousLastDoc,
          loadId,
          latestLoadIdRef,
        });
        if (simple.stale) {
          return;
        }
        const pageDocs = simple.pageDocs;
        if (pageDocs.length > 0) {
          lastDocPerPage.current[targetPage] = pageDocs[pageDocs.length - 1];
          lastDocPerPage.current.length = targetPage + 1;
        } else {
          lastDocPerPage.current[targetPage] = null;
        }
        setRows(simple.mapped);
        setHasNext(simple.hasNext);
        setPage(targetPage);
      } catch (err) {
        if (loadId !== latestLoadIdRef.current) {
          return;
        }
        console.error(err);
        setError(err);
      } finally {
        if (loadId === latestLoadIdRef.current) {
          setLoading(false);
        }
      }
    },
    [pageSize, statusFilter, outcomeFilter, typeFilter, scheduleFilter, targetFilter],
  );

  useEffect(() => {
    loadPage(0).catch(function crawlExecutionsInitialLoadError() {
      /* errors handled inside loadPage */
    });
  }, [loadPage]);

  const handlePageSizeChange = useCallback((event) => {
    const nextSize = Number(event.target.value);
    lastDocPerPage.current = [];
    scanResumeAfterPage.current = [];
    setPageSize(nextSize);
    setPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  const handleOutcomeFilterChange = useCallback((event) => {
    setOutcomeFilter(event.target.value);
  }, []);

  const handleTypeFilterChange = useCallback((event) => {
    setTypeFilter(event.target.value);
  }, []);

  const handlePrevPage = useCallback(() => {
    loadPage(page - 1).catch(function crawlExecutionsPrevPageError() {
      /* errors handled inside loadPage */
    });
  }, [loadPage, page]);

  const handleNextPage = useCallback(() => {
    loadPage(page + 1).catch(function crawlExecutionsNextPageError() {
      /* errors handled inside loadPage */
    });
  }, [loadPage, page]);

  const handleScheduleInputChange = useCallback(function handleScheduleInputChange(event) {
    setScheduleInput(event.target.value);
  }, []);

  const handleTargetInputChange = useCallback(function handleTargetInputChange(event) {
    setTargetInput(event.target.value);
  }, []);

  const handleCompareClick = useCallback(function handleCompareClick(row) {
    setCompareExecution(row);
  }, []);

  const handleCompareDialogClose = useCallback(function handleCompareDialogClose() {
    setCompareExecution(null);
  }, []);

  const toolbar = (
    <Box
      display="flex"
      flexWrap="wrap"
      justifyContent="flex-end"
      alignItems="center"
      gap={2}
      mb={2}
    >
      <TextField
        size="small"
        label="Schedule contains"
        placeholder="Schedule ID substring"
        value={scheduleInput}
        onChange={handleScheduleInputChange}
        sx={{ minWidth: 220, flex: '1 1 200px' }}
      />
      <TextField
        size="small"
        label="Target contains"
        placeholder="Target ID substring"
        value={targetInput}
        onChange={handleTargetInputChange}
        sx={{ minWidth: 200, flex: '1 1 180px' }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="exec-status-filter-label">Status</InputLabel>
        <Select
          labelId="exec-status-filter-label"
          label="Status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
        >
          {EXECUTION_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="exec-outcome-filter-label">Outcome</InputLabel>
        <Select
          labelId="exec-outcome-filter-label"
          label="Outcome"
          value={outcomeFilter}
          onChange={handleOutcomeFilterChange}
        >
          {EXECUTION_OUTCOME_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="exec-type-filter-label">Type</InputLabel>
        <Select
          labelId="exec-type-filter-label"
          label="Type"
          value={typeFilter}
          onChange={handleTypeFilterChange}
        >
          <MenuItem value="">All</MenuItem>
          {CRAWL_TYPES.map((crawlTypeOption) => (
            <MenuItem key={crawlTypeOption} value={crawlTypeOption}>
              {crawlTypeOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="exec-page-size-label">Rows per page</InputLabel>
        <Select
          labelId="exec-page-size-label"
          label="Rows per page"
          value={pageSize}
          onChange={handlePageSizeChange}
        >
          {PAGE_SIZE_OPTIONS.map((sizeOption) => (
            <MenuItem key={sizeOption} value={sizeOption}>
              {sizeOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const errorBanner =
    error ? (
      <Typography color="error" sx={{ mb: 2 }}>
        {error.message}
      </Typography>
    ) : null;

  const mainContent = loading ? (
    <Box display="flex" justifyContent="center" py={4}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <CrawlExecutionsTable rows={rows} onCompareClick={handleCompareClick} />
      <ExecutionsPagerBar
        page={page}
        rowsLength={rows.length}
        hasNext={hasNext}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />
    </>
  );

  return (
    <Box>
      {toolbar}
      {errorBanner}
      {mainContent}
      <ExecutionCompareDialog
        open={compareExecution != null}
        onClose={handleCompareDialogClose}
        execution={compareExecution}
      />
    </Box>
  );
}

export default CrawlExecutionsTab;
